import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, injectIntl } from 'react-intl';
import naturalSortBy from '@bibliocommons/natural-sort-by';
import { Table } from '@bibliocommons/deprecated-base-table';
import { bindAll } from '@bibliocommons/utils-react';
import injectLibrary from 'app/components/hocs/injectLibrary';
import { StackMapLinkContainer } from 'app/components/bibs/shared/StackMap/StackMapLink';

import './AvailabilityTable.scss';

export class AvailabilityTable extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  static handleAlphanumericSort(items, sortField, sortDirection) {
    return items.sort(naturalSortBy(sortField, sortDirection));
  }

  shouldRenderStackMap(item) {
    const { stackMap } = this.props;

    return (
      stackMap.get('enabled') &&
      item.get('collection') &&
      item.get('callNumber') &&
      stackMap.get('supportedBranches').includes(item.getIn(['branch', 'name'])) &&
      item.getIn(['availability', 'statusType']) !== 'UNAVAILABLE'
    );
  }

  renderStatus(value, index) {
    const item = this.props.rows.get(index);
    const stackMap = this.props.stackMap;
    const renderStackMapLink = this.shouldRenderStackMap(item);
    const handleClick = () => this.props.openStackMap(stackMap, item);

    if (renderStackMapLink) {
      return (
        <>
          <span> {value}</span>
          <StackMapLinkContainer bibItem={item} handleClick={handleClick} />
        </>
      );
    }

    return value;
  }

  render() {
    const { intl, rows, status, singleBranch, showDueDateInCirc, showCollectionInCirc } = this.props;
    const count = rows.size;
    const inSiteScope = rows.first().get('inSiteScope');
    const statusType = rows.first().getIn(['availability', 'statusType']);
    const statusId = `circ_group_${status.replace(/EXTENDED_|IN_SCOPE_/, '').toLowerCase()}`;

    let fields = [
      {
        key: 'callNumber',
        type: 'String',
        sort: AvailabilityTable.handleAlphanumericSort
      },
      {
        key: 'volume',
        type: 'String',
        sort: AvailabilityTable.handleAlphanumericSort
      },
      {
        key: 'copy',
        type: 'String',
        sort: AvailabilityTable.handleAlphanumericSort
      },
      {
        key: 'availability.libraryStatus',
        type: 'String',
        sort: AvailabilityTable.handleAlphanumericSort
      }
    ];

    if (showCollectionInCirc) {
      fields = [
        {
          key: 'collection',
          type: 'String',
          sort: AvailabilityTable.handleAlphanumericSort,
          transformationMap: this.renderStatus
        },
        ...fields
      ];
    }

    if (showDueDateInCirc) {
      fields = [
        ...fields,
        {
          key: 'dueDate',
          type: 'Date',
          sort: AvailabilityTable.handleAlphanumericSort
        }
      ];
    }

    if (!singleBranch) {
      fields = [
        {
          key: 'branchName',
          type: 'String',
          sort: AvailabilityTable.handleAlphanumericSort
        },
        ...fields
      ];
    }

    return (
      <div className="cp-availability-table">
        <FormattedMessage
          id="CircTableTitle"
          values={{
            status: intl.formatMessage({
              id: statusId
            }),
            count,
            inSiteScope: inSiteScope ? 'yes' : 'no'
          }}
        >
          {string => <h4 className={statusType.toLowerCase()}>{string}</h4>}
        </FormattedMessage>
        <Table rows={rows} fields={fields} sortable />
      </div>
    );
  }
}

AvailabilityTable.propTypes = {
  rows: ImmutablePropTypes.list.isRequired,
  status: PropTypes.string.isRequired,
  singleBranch: PropTypes.bool.isRequired,

  stackMap: ImmutablePropTypes.mapContains({
    enabled: PropTypes.bool,
    branches: ImmutablePropTypes.listOf(PropTypes.string),
    domain: PropTypes.string
  }),
  openStackMap: PropTypes.func.isRequired,
  showDueDateInCirc: PropTypes.bool,
  showCollectionInCirc: PropTypes.bool
};

AvailabilityTable.displayName = 'AvailabilityTable';

const mapLibraryToProps = ({ library }) => ({
  singleBranch: library.get('singleBranch')
});

export default injectIntl(injectLibrary(mapLibraryToProps)(AvailabilityTable));
